import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { makeStyles } from 'src/makeStyles';
import { IconButton } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import cn from 'src/classnames';
import usePageContext from 'utils/hooks/usePageContext';
import { PRIVACY_ATTRIBUTE_IDS, URL_STATIC_PATHS, VALID_QUERY_PARAMS } from 'constants/index';
import { ENTITY_TYPES } from 'operabase-router/constants';
import Typography from 'components/uiLibrary/Typography';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import useTracking from 'components/Globals/Analytics';
import { useTrackingClickStyles } from 'components/Globals/TrackingTester/TrackingTester';
import EntityName from 'components/Globals/EntityName';
import { TP, TRANSFORMATION_ARTIST_PROFILE_LISTING, ENTITY_ACTION_TYPES, AGENCY_ACTORS } from 'constants/consts';
import EntityCard from 'components/Globals/EntityCard';
import DeleteFbIcon from 'components/Icons/DeleteFbIcon';
import { COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import { useMutation } from 'utils/react-query';
import { useManagerConnections } from 'utils/hooks/useManagerQueries';
import queries from 'containers/Managers/queries';

import LinkButton from 'components/uiLibrary/LinkButton';
import email from '../../../public/images/icons/email.png';
import phone from '../../../public/images/icons/phone.png';
import ImageTag from '../../../components/ImageTag';
import { colors, fontSize } from '../../../constants/styleConstants';
import { useFBTableStyles } from '../../../components/FbTable';
import FbTableCard, { useTableCardStyles } from '../../../components/FbTable/FbTableCard';
import NextLink from '../../../components/NextLink';
import { getAgreementCountries } from '../../../utils/agency';
import { AGREEMENTS_TYPES } from '../constants';

const useStyles = makeStyles()(theme => ({
  agreementsInfo: {
    textTransform: 'capitalize',
  },
  regionItem: {
    padding: '0px 5px',
    color: '#565656',
    fontSize: fontSize.smallTitle,
    borderRadius: 1.4,
    backgroundColor: `${colors.background.default} !important`,
    fontWeight: 400,
    marginRight: 5,
    display: 'inline-block',
    wordBreak: 'break-word',
  },
  innerCell: {
    borderLeft: `1px solid ${colors.linesGray}`,
  },
  avatar: {
    marginRight: 10,
  },
  artistInfo: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  artistNameBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    width: '100%',
    padding: `10px`,
    position: 'relative',
  },
  editButtonsBlock: {
    marginLeft: 'auto',
    maxWidth: 80,
    padding: 10,
    display: 'flex',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%',
      justifyContent: 'flex-end',
      marginLeft: '0',
      alignItems: 'end',
      gap: 10,
      flexDirection: 'column',
    },
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '18px',
    color: colors.blueChip,
    marginRight: 40,
    marginLeft: 40,
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
      marginLeft: 0,
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  label: {
    color: colors.subTitle,
  },
  profession: {
    color: colors.darkText,
    fontSize: fontSize.medium,
    fontWeight: 'bold',
  },
  requestLink: {
    color: 'black',
    whiteSpace: 'nowrap',
  },
  agencyLink: {
    display: 'block',
  },
  greyChip: {
    textTransform: 'uppercase',
    backgroundColor: colors.background.default,
  },
  agreementType: {
    color: colors.cardTitle,
    fontSize: fontSize.smallTitle,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  generalManager: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  representative: {
    fontSize: fontSize.medium,
  },
  includeExclude: {
    textTransform: 'capitalize',
  },
  representativeName: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  exclusiveTerritories: {
    marginRight: 20,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  agencyRow: {
    width: '100%',
  },
  cropedImage: {
    height: TRANSFORMATION_ARTIST_PROFILE_LISTING.height,
    width: TRANSFORMATION_ARTIST_PROFILE_LISTING.width,
    borderRadius: TRANSFORMATION_ARTIST_PROFILE_LISTING.radius,
    padding: '3px',
    marginRight: '5px',
  },
  icon: {
    width: '47px',
    height: '47px',
    marginRight: '5px',
  },
  disableLink: {
    pointerEvents: 'none',
    cursor: 'default',
  },
  tableHead: {
    background: colors.lightGrey,
    color: colors.grey1,
    textTransform: 'uppercase',
  },
  iconButton: { marginRight: '5px' },
  shareButton: {
    minWidth: '135px',
    height: '33px',
    background: colors.blueChip,
    border: `1px solid ${colors.blue4}`,
    borderRadius: '8px',
    textTransform: 'uppercase',
    '&:hover': {
      background: colors.blue4,
    },
  },
  addButton: {
    background: '#0067C5',
    width: '95px',
    height: '33px',
    border: '1px solid #005099',
    borderRadius: '8px',
    textTransform: 'uppercase',
    color: 'white',
    fontSize: '16px',
    lineHeight: '19px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  hideButton: {
    background: 'transparent',
    width: '95px',
    height: '33px',
    border: '1px solid #005099',
    borderRadius: '8px',
    textTransform: 'uppercase',
    color: '#0067C5',
    fontSize: '16px',
    lineHeight: '19px',
    fontWeight: 700,
    marginRight: '10px',
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      marginBottom: '10px',
    },
  },
  actionPending: {
    minWidth: '135px',
    color: colors.blueChip,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  more: {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '14px',
    padding: '8px',
  },
  moreActive: {
    color: colors.blueChip,
    background: '#DAE3FA',
    borderRadius: '8px',
  },
  arrowIcon: {
    marginLeft: '5px',
    height: '15px',
    width: '15px',
  },
  notification: {
    display: 'flex',
    paddingRight: '15px',
  },
  notificationLink: {
    whiteSpace: 'nowrap',
    color: '#0067C5',
    fontWeight: 600,
    [theme.breakpoints.down('md')]: {
      fontWeight: 600,
      fontSize: '14px',
    },
  },
  notificationNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '5px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#F36D2A',
    color: 'white',
    minWidth: '22px',
  },
  notApproved: {
    color: '#8E021A',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  artistNameBlock_cell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  editButtonsBlock_artist: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  deleteIcon: {
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      right: 10,
      top: 10,
    },
  },
}));

export const AgreementsData = React.memo(({ agreements, t, commonClasses, acknowledged }) => {
  const { classes } = useStyles();
  const generalManagementIndex = agreements.findIndex(
    agr => get(agr, 'agreementType') === AGREEMENTS_TYPES.GENERAL_MENEGMENT,
  );
  const isGeneralManagement = generalManagementIndex !== -1;
  let noGeneralManagementAgreements = agreements;
  if (isGeneralManagement) {
    noGeneralManagementAgreements = agreements.filter(
      agr => get(agr, 'agreementType', '') !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
    );
  }
  const contactPersons = get(agreements, '0.contactPersons', []);

  return (
    <>
      {isGeneralManagement && (
        <span className={commonClasses.generalManager}>{t(`${TP}.m_EDIT_ROSTER_LIST_GENERALMANAGER`)}</span>
      )}
      {!acknowledged && (
        <Typography size="11" className={classes.notApproved}>
          <SpriteIcon size={13} icon="report_error" /> {t('AGENCY_NOT_CONFIRMED')}
        </Typography>
      )}
      {noGeneralManagementAgreements.map(agreement => (
        <div key={get(agreement, 'id')}>
          <span className={commonClasses.agreementType}>{get(agreement, 'agreementType', '')}</span>{' '}
          {get(agreement, 'exclusive') ? (
            <span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_E`)}</span>
          ) : (
            <span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_NE`)}</span>
          )}
          {': '}
          {get(agreement, 'includedCountries', []).map(region => (
            <span key={`includedCountries-${get(region, 'id')}`} className={classes.regionItem}>
              {get(region, 'name', '')}
            </span>
          ))}
          {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
            <>
              {`${t(`${TP}.REPRESENTATION_EXCLUDING`)}: `}
              {get(agreement, 'excludedCountries', []).map(region => (
                <span key={`excludedCountries-${get(region, 'id')}`} className={classes.regionItem}>
                  {get(region, 'name', '')}
                </span>
              ))}
            </>
          )}
        </div>
      ))}
      {contactPersons.length > 0 && (
        <div>
          <span className={commonClasses.representative}>{t(`${TP}.FN_AGENCY_REPRESENTATIVE`)}: </span>
          {contactPersons.map((el, index) => (
            <span className={commonClasses.representativeName}>
              {el?.employee?.name}
              {contactPersons[index + 1] ? ', ' : ''}
            </span>
          ))}
        </div>
      )}
    </>
  );
});

export const AgreementsDataV1 = React.memo(
  ({ agreements, t, professionsAgreement, agencyProfessioMeta, commonClasses, contactPersons }) => {
    const { classes } = useStyles();

    const agreementIdList = agreements?.map(({ connection: { id } }) => id);

    const agreementList = Object.keys(professionsAgreement)
      ?.map(key => professionsAgreement[key])
      ?.map(aAgreement =>
        aAgreement
          ?.map(agreement => ({
            ...agreement,
            ...getAgreementCountries(agreement),
          }))
          ?.filter(
            agr =>
              agreementIdList?.includes(agr?.connection?.id) &&
              agr?.agreementType !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
          ),
      );

    return (
      <>
        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>{t(`${TP}.FN_PROFESSION_VOICE_TYPE`)}</TableCell>
                <TableCell>{t(`${TP}.FN_REPRESENTATIVE_INFO`)}</TableCell>
                <TableCell>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agreementList?.map(agr => (
                <>
                  <TableCell>
                    {agr?.length > 0 && (
                      <>
                        <div className={classes.profession}>
                          {agencyProfessioMeta?.[agr?.[0]?.agencyConnectionProfession?.id]?.name ?? '-'}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {contactPersons.length > 0 && (
                      <div className={classes.agentDetails}>
                        {contactPersons.map(cp => (
                          <div className={classes.agentProfile}>
                            {cp?.profile?.image && <ImageTag src={cp?.profile?.image} alt="profile" />}
                            <span>{cp?.agentName}</span>
                            {cp.agentPhoneNumber && (
                              <div className={classes.repInfo}>
                                <ImageTag src={phone} alt="phone" />
                                {cp.agentPhoneNumber}
                              </div>
                            )}
                            {cp.agentEmail && (
                              <div className={classes.repInfo}>
                                <ImageTag src={email} alt="email" />
                                <a href={`mailto:${cp.agentEmail}`}>{cp.agentEmail}</a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={classes.representationDetailsWrapper}>
                      <div className={classes.representationDetailsTerritories}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            <div className={classes.exclusiveTerritories}>
                              <span className={commonClasses.label}>
                                {`"${get(agreement, 'agreementType', '')}"`}{' '}
                                {get(agreement, 'exclusive') ? t(`${TP}.m_TERRITORIES_E`) : t(`${TP}.m_TERRITORIES_NE`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={classes.includeWrapper}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            <div className={classes.countries}>
                              <span className={classes.includeExclude}>{`${t(`${TP}.REPRESENTATION_INCLUDE`)} `}</span>
                              {get(agreement, 'includedCountries', []).map(region => (
                                <>
                                  <span className={classes.regionItem} key={`includedCountries-${get(region, 'id')}`}>
                                    {get(region, 'name', '')}
                                  </span>
                                </>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={classes.excludeWrapper}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
                              <>
                                <span className={classes.includeExclude}>{`${t(
                                  `${TP}.REPRESENTATION_EXCLUDING`,
                                )} `}</span>
                                {get(agreement, 'excludedCountries', []).map(region => (
                                  <span className={classes.regionItem} key={`excludedCountries-${get(region, 'id')}`}>
                                    {get(region, 'name', '')}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </TableCell>
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  },
);

const EditButtons = ({
  deleteFunction,
  classes,
  agreementPageLinkProps,
  t,
  trackingData = {},
  skipTracking = false,
}) => {
  const track = useTracking();
  const trackingClasses = useTrackingClickStyles(trackingData, skipTracking);

  return (
    <>
      <NextLink
        href={agreementPageLinkProps.href}
        as={agreementPageLinkProps.as}
        type="text"
        className={classes.editButton}
        trackingData={{
          component: COMPONENTS.REPRESENTATION_DETAILS,
          subComponent: SUB_COMPONENTS.EDIT_CTA,
          ...trackingData,
        }}
      >
        <EditIcon className={classes.iconButton} />
        {t(`${TP}.FN_EDIT_REPRESENTATION_DETAILS`)}
      </NextLink>
      <>
        <div className={classes.deleteIcon}>
          <IconButton
            className={trackingClasses}
            onClick={() => {
              track.click({
                component: COMPONENTS.REQUEST_ACCESS,
                subComponent: SUB_COMPONENTS.DELETE_CTA,
                ...trackingData,
              });
              deleteFunction();
            }}
          >
            <DeleteFbIcon color="black" />
          </IconButton>
        </div>
      </>
    </>
  );
};

const AddButtons = ({
  classes,
  entityConnection,
  t,
  deleteArtistFromRoster,
  trackingData = {},
  skipTracking = false,
}) => {
  const track = useTracking();
  const trackingClasses = useTrackingClickStyles(trackingData, skipTracking);
  const { entity } = usePageContext();
  const handleHide = () => {
    deleteArtistFromRoster(null, entityConnection.id);
    track.click({ subComponent: SUB_COMPONENTS.REJECT_CTA, ...trackingData });
  };

  const { refetch: refetchManagerConnections } = useManagerConnections({ enabled: false });
  const { mutate: createAgencyActions, isLoading } = useMutation(queries.createAgencyActions());

  const handleAdd = () => {
    const agencyId = entity?.id;

    const payload = {
      id: entityConnection?.id,
      type: ENTITY_ACTION_TYPES.UPDATE_AGENCY_CONNECTION_STATUS,
      privacyAttributes: [{ id: PRIVACY_ATTRIBUTE_IDS.PUBLIC }],
      actor: AGENCY_ACTORS.AGENCY,
    };

    createAgencyActions(
      {
        data: payload,
        endpointParams: {
          id: agencyId,
        },
      },
      {
        onSuccess: () => {
          refetchManagerConnections();
        },
      },
    );

    track.click({ subComponent: SUB_COMPONENTS.ACCEPT_CTA, ...trackingData });
  };

  return (
    <>
      <button
        onClick={handleHide}
        type="button"
        className={cn(classes.hideButton, trackingClasses)}
        disabled={isLoading}
      >
        {t('REJECT')}
      </button>

      <button onClick={handleAdd} type="button" className={cn(classes.addButton, trackingClasses)} disabled={isLoading}>
        {t(`${TP}.Accept`)}
      </button>
    </>
  );
};

export const ArtistsRow = ({
  data,
  showAdditionalAgents,
  editMode,
  addMode,
  t,
  deleteArtistClick,
  agencyLinkProps,
  userProfiles,
  onRequest,
  deleteArtistFromRoster,
  trackingData = {},
}) => {
  const { classes, cx } = useFBTableStyles();
  const { classes: commonClasses } = useStyles();
  const { navigate } = usePageContext();

  const isArtistEntity = !!data?.artist?.profile?.id;

  const onClickEdit = profile => {
    navigate.to(
      navigate.getLinkProps({
        entityType: ENTITY_TYPES.PROFILE,
        entity: profile,
        onlyLinkProps: true,
        edit: true,
      }),
    );
  };

  return (
    <TableRow key={get(data, 'id')}>
      <TableCell className={cx(classes.cell, classes.artistName)}>
        <div className={classes.flexInlineCell}>
          {isArtistEntity && (
            <div className={cx(commonClasses.artistNameBlock_cell)}>
              <EntityCard entity={data.artist.profile} entityType={ENTITY_TYPES.PROFILE} trackingData={trackingData} />
              {editMode && (
                <div className={commonClasses.editButtonsBlock_artist} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <LinkButton variant="text" onClick={() => onClickEdit(data?.artist?.profile)}>
                    {t(`${TP}.FN_MANAGE_PROFILE`)}
                  </LinkButton>
                  <LinkButton variant="text" onClick={() => onClickEdit(data?.artist?.profile)}>
                    {t(`${TP}.FN_EDIT_FEAT_VISIBILITY_REPORT`)}
                  </LinkButton>
                </div>
              )}
            </div>
          )}
          {data?.organization?.id && (
            <EntityCard entity={data.organization} entityType={ENTITY_TYPES.ORGANIZATION} trackingData={trackingData} />
          )}
        </div>
      </TableCell>
      <TableCell className={cx(classes.cell, commonClasses.innerCell)}>
        <AgreementsData
          acknowledged={data?.acknowledged}
          agreements={get(data, 'agreements', [])}
          t={t}
          commonClasses={commonClasses}
        />
      </TableCell>
      {showAdditionalAgents && (
        <TableCell className={cx(classes.cell, commonClasses.innerCell)} style={{ width: '20%' }}>
          {get(data, 'artist.profile.agencyOrganizations', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...navigate.getLinkProps({
                entityType: ENTITY_TYPES.AGENCY,
                entity: agency,
              })}
              trackingData={{
                entityId: agency?.id,
                entityName: agency?.name,
                entityType: ENTITY_TYPES.AGENCY,
                component: COMPONENTS.ADDITIONAL_AGENTS,
                subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
                ...trackingData,
              }}
            >
              <EntityName
                entityType={ENTITY_TYPES.AGENCY}
                entity={agency}
                trackingData={{ component: COMPONENTS.ADDITIONAL_AGENTS, ...trackingData }}
                isRaw
              />
            </NextLink>
          ))}
        </TableCell>
      )}

      {addMode && (
        <TableCell style={{ width: '10%', minWidth: '340px' }}>
          <div className={classes.flexInlineCell} style={{ justifyContent: 'flex-end' }}>
            <AddButtons
              entityConnection={data}
              classes={commonClasses}
              t={t}
              deleteArtistFromRoster={deleteArtistFromRoster}
              trackingData={{ component: COMPONENTS.ADDITIONAL_AGENTS, ...trackingData }}
            />
          </div>
        </TableCell>
      )}

      {editMode && (
        <TableCell style={{ width: '10%', minWidth: '340px' }}>
          <div className={classes.flexInlineCell} style={{ justifyContent: 'flex-end' }}>
            <EditButtons
              targetProfile={data?.artist?.profile}
              userProfiles={userProfiles}
              onRequest={onRequest}
              deleteFunction={() => deleteArtistClick(data)}
              classes={commonClasses}
              agreementPageLinkProps={agencyLinkProps.getSubPath({
                path: `${URL_STATIC_PATHS.EDIT}/${URL_STATIC_PATHS.AGENCY_ROSTER_UPDATE}`,
                queryParams: { [VALID_QUERY_PARAMS.AGENCY_CONNECTION_ID]: data?.id },
              })}
              t={t}
              trackingData={trackingData}
              disableProfileRequest={!isArtistEntity}
            />
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

ArtistsRow.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
};

export const ArtistsCard = ({
  data,
  showAdditionalAgents,
  editMode,
  addMode,
  t,
  deleteArtistClick,
  agencyLinkProps,
  userProfiles,
  onRequest,
  deleteArtistFromRoster,
  trackingData = {},
}) => {
  const { classes } = useTableCardStyles();
  const { classes: commonClasses } = useStyles();
  const { navigate } = usePageContext();

  const isArtistEntity = !!data?.artist?.profile?.id;

  const onClickEdit = profile => {
    navigate.to(
      navigate.getLinkProps({
        entityType: ENTITY_TYPES.PROFILE,
        entity: profile,
        onlyLinkProps: true,
        edit: true,
      }),
    );
  };

  return (
    <FbTableCard key={get(data, 'id')}>
      <div className={classes.mainInfoElements}>
        <div className={commonClasses.artistNameBlock}>
          {isArtistEntity && (
            <EntityCard entity={data.artist.profile} entityType={ENTITY_TYPES.PROFILE} trackingData={trackingData} />
          )}
          {data?.organization?.id && (
            <EntityCard entity={data.organization} entityType={ENTITY_TYPES.ORGANIZATION} trackingData={trackingData} />
          )}

          {addMode && (
            <div className={commonClasses.editButtonsBlock}>
              <AddButtons
                entityConnection={data}
                classes={commonClasses}
                t={t}
                deleteArtistFromRoster={deleteArtistFromRoster}
                trackingData={trackingData}
              />
            </div>
          )}
          {editMode && (
            <div className={commonClasses.editButtonsBlock}>
              {editMode && (
                <div className={commonClasses.editButtonsBlock_artist}>
                  <LinkButton variant="text" onClick={() => onClickEdit(data?.artist?.profile)}>
                    {t(`${TP}.FN_MANAGE_PROFILE`)}
                  </LinkButton>{' '}
                  {!data?.organization?.id && (
                    <>
                      {' | '}
                      <LinkButton variant="text" onClick={() => onClickEdit(data?.artist?.profile)}>
                        {t(`${TP}.FN_EDIT_FEAT_VISIBILITY_REPORT`)}
                      </LinkButton>
                    </>
                  )}
                </div>
              )}
              <EditButtons
                targetProfile={data?.artist?.profile}
                userProfiles={userProfiles}
                deleteFunction={() => deleteArtistClick(data)}
                classes={commonClasses}
                agreementPageLinkProps={agencyLinkProps.getSubPath({
                  path: `${URL_STATIC_PATHS.EDIT}/${URL_STATIC_PATHS.AGENCY_ROSTER_UPDATE}`,
                  queryParams: { [VALID_QUERY_PARAMS.AGENCY_CONNECTION_ID]: data?.id },
                })}
                mobile
                onRequest={onRequest}
                t={t}
                trackingData={trackingData}
                disableProfileRequest={!isArtistEntity}
              />
            </div>
          )}
        </div>
      </div>

      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</span>
        <AgreementsData
          acknowledged={data?.acknowledged}
          agreements={get(data, 'agreements', [])}
          t={t}
          commonClasses={commonClasses}
        />
      </div>

      {showAdditionalAgents && (
        <div className={classes.infoElement}>
          <span className={classes.infoTitle}>{t(`${TP}.m_OTHERMANAGERSOFTHISARTIST`)}</span>
          {get(data, 'artist.profile.agencyOrganizations', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...navigate.getLinkProps({
                entityType: ENTITY_TYPES.AGENCY,
                entity: agency,
              })}
            >
              {agency?.name}
            </NextLink>
          ))}
        </div>
      )}
    </FbTableCard>
  );
};

ArtistsCard.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
};
